/**
 * @fileoverview Subheader on top of page still (below header) navigation and controls
 * @description Displays main navigation (Research dropdown), search input (contextual),
 * and user controls (Notifications, Profile dropdown/Login/Signup buttons) based on auth state
 * Not to be confused with @see {@link src/components/footer/Footer.tsx}
 * @active Client component [CONFIRMED]
 *
 * @see {@link src/components/ui/header/Header.tsx} - Likely parent component
 * @see {@link src/providers/supabaseAuthProvider.client.tsx} - Provides auth state (`useSupabaseAuth`)
 * @see {@link src/components/ui/dropdown/Dropdown.jsx} - Renders navigation/profile menus
 * @see {@link src/components/notification/NotificationMenu.tsx} - Renders notification icon/menu
 * @see {@link src/services/profile.service.ts} - Used for fetching user profile
 * @see {@link src/services/notifications.service.ts} - Used for fetching notifications
 * @see {@link src/components/ui/header/components/WaitlistCTANavButton.tsx} - Temporary waitlist CTA replacement
 */

"use client";

import { useEffect, useState } from "react";

import { Flex, Text } from "@chakra-ui/react";
import Link from "next/link";
import { usePathname, useRouter } from "next/navigation";
import { Cookies } from "react-cookie";
import { useSelector, useDispatch } from "react-redux";

import {
  menuListProfile,
  menuListResearch,
} from "@/assets/data/menuList/menuList";
import ArrowRightIcon from "@/assets/svg/arrow_right.svg";
import SearchSvg from "@/assets/svg/search.svg";
import SearchCompanies from "@/components/companies/companiesList/newCompaniesList/SearchCompanies";
import { NotificationMenu } from "@/components/notification/NotificationMenu";
import { useBreakPoints } from "@/hooks/useBreakPoints";
import { useSupabaseAuth } from "@/providers/supabaseAuthProvider.client"; // Import the new hook
import {
  useLazyGetNotificationsQuery,
  useLazyGetUnreadNotificationsQuery,
} from "@/services/notifications.service";
import { useLazyGetUserProfileQuery } from "@/services/profile.service";
import { useLazyGetSearchDataQuery } from "@/services/search.service";
import newCompaniesListSelectors from "@/store/newCompaniesList/newCompaniesList.selectors";
import { getUserProfileInfo } from "@/store/profile/profile.selectors";
import { h_700_16_100_32 } from "@/styles/fontStyles";
import type { UserProfile } from "@/types/userAuth";
import { logSilentError } from "@/services/error.service";

import AuthInput from "../auth/AuthInput";
import ButtonDefault from "../buttons/DefaultBtn";
import { Dropdown } from "../dropdown/Dropdown";
import { createClientComponent } from "@/app/utils/supabase/client";
// Import the temporary WaitlistCTA component
import WaitlistCTANavButton from "./components/WaitlistCTANavButton";
// Import the TestSupabaseAuthButton component
import TestSupabaseAuthButton from "./components/TestSupabaseAuthButton.client";
// Import the tRPC client
import { trpc } from "@/lib/trpc/client";

/**
 * Displays subheader with navigation, search, and auth controls
 *
 * @component SubHeader
 * @param {object} props - Component props (currently only deprecated userIsVerify)
 * @returns {JSX.Element} Rendered subheader component
 */
export const SubHeader = ({
  userIsVerify: _deprecatedUserIsVerify, // Prop is deprecated, authStatus derived internally
}) => {
  const router = useRouter();
  const pathName = usePathname();
  // Removed setUserSignUpInfo from useActions() destructuring
  const { isMobile } = useBreakPoints();
  const profile = useSelector(getUserProfileInfo()) as UserProfile | null;
  const [searchData, setSearchData] = useState("");
  const [getUserProfile, { isLoading: profileLoading }] =
    useLazyGetUserProfileQuery();
  const [getNotifications] = useLazyGetNotificationsQuery();
  const [getUnreadNotifications] = useLazyGetUnreadNotificationsQuery();
  const [getSearchData] = useLazyGetSearchDataQuery();
  const { user, isLoading: isAuthLoading } = useSupabaseAuth(); // Use the new hook
  const authStatus = !!user && !isAuthLoading; // Derive authStatus
  const cookies = new Cookies();
  // Removed isLoggedIn cookie check as Supabase handles session
  const isList = pathName?.endsWith("/companies");
  const isCards = useSelector(newCompaniesListSelectors.getIsCompaniesCards());
  const dispatch = useDispatch();
  
  // Add tRPC error logging
  const logError = trpc.system.logClientError.useMutation();

  /**
   * Handles Enter key press in search input; triggers search and navigates
   * @param {React.KeyboardEvent<HTMLInputElement>} e - Keyboard event
   */
  const handlerKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    const target = e.target as HTMLInputElement;
    if (e.key === "Enter" && target.value?.trim() !== "") {
      getSearchData({ search: target.value });
      router.push(`/search?search=${target.value}`);
    }
  };

  /**
   * Handles search icon click; triggers search and navigates
   */
  const onClickIconLeft = () => {
    if (searchData?.trim() !== "") {
      getSearchData({ search: searchData }); // Use state variable 'searchData'
      router.push(`/search?search=${searchData}`); // Use state variable 'searchData'
    }
  };

  /**
   * Placeholder for clearing user info on signup link click (action removed)
   */
  const clearUserInfoHandler = () => {
    // Action `setUserSignUpInfo` was removed from Redux
    console.log(
      "[SubHeader] clearUserInfoHandler called, but related action is deprecated/removed"
    );
  };

  useEffect(() => {
    setSearchData("");
  }, [pathName]);

  // Consolidated user profile and notification data fetching
  useEffect(() => {
    // Only fetch data if authentication state appears valid and profile isn't loading
    if (authStatus && !profileLoading) {
      const fetchUserData = async (): Promise<void> => {
        try {
          console.log(
            "[SubHeader] Auth status is valid, fetching user profile"
          );
          // Get user profile first
          const profileResult = await getUserProfile({});

          if (profileResult.data) {
            console.log(
              "[SubHeader] Profile loaded successfully, fetching notifications"
            );
            // Only attempt to fetch notifications if profile request succeeded
            await Promise.allSettled([
              getNotifications({}),
              getUnreadNotifications({}),
            ]);
          } else if (profileResult.error) {
            console.warn(
              "[SubHeader] Profile fetch error:",
              profileResult.error
            );
            
            // Log to server via tRPC with more context
            logError.mutate({
              source: 'profile-service',
              component: 'SubHeader.fetchUserData',
              severity: 'error',
              details: {
                message: String((profileResult.error as any)?.message || 'Unknown profile fetch error'),
                errorType: "PROFILE_FETCH_ERROR",
                timestamp: new Date().toISOString(),
                url: typeof window !== 'undefined' ? window.location.href : '',
                // Include useful context but no sensitive data
                context: {
                  authStatus,
                  path: pathName,
                  isAuthLoading,
                  errorType: typeof profileResult.error
                }
              }
            });
            
            // Also use existing error service
            logSilentError(profileResult.error, "SubHeader.fetchUserData.profileFetch");
          }
        } catch (error) {
          console.warn("[SubHeader] Error in fetchUserData:", error);
          
          // Log to server via tRPC
          logError.mutate({
            source: 'profile-service',
            component: 'SubHeader.fetchUserData',
            severity: 'error',
            details: {
              message: error instanceof Error ? error.message : String(error),
              errorType: "PROFILE_FETCH_EXCEPTION",
              timestamp: new Date().toISOString(),
              url: typeof window !== 'undefined' ? window.location.href : '',
              context: {
                authStatus,
                hasUser: !!user
              }
            }
          });
          
          // Also use existing error service
          logSilentError(error, "SubHeader.fetchUserData");
        }
      };

      const timeoutId = setTimeout(fetchUserData, 100);
      return () => clearTimeout(timeoutId);
    } else {
      // Log skipping with reason and context to help debugging
      const skipReason = !authStatus ? "invalid auth status" : "profile is loading";
      console.log(`[SubHeader] Skipping profile/notification fetch: ${skipReason}`, {
        authStatus,
        profileLoading,
      });
      
      // Also log to tRPC for visibility in server logs when there are frequent auth issues
      if (!authStatus && !isAuthLoading) {
        logError.mutate({
          source: 'auth-service',
          component: 'SubHeader.skipFetch',
          severity: 'warning',
          details: {
            message: `Profile/notifications fetch skipped: ${skipReason}`,
            errorType: "AUTH_STATE_ISSUE",
            timestamp: new Date().toISOString(),
            url: typeof window !== 'undefined' ? window.location.href : '',
            context: {
              authStatus,
              isAuthLoading,
              hasUser: !!user
            }
          }
        });
      }
    }

    // Return a noop function when the condition is false
    return () => {};
  }, [
    authStatus,
    getUserProfile,
    getNotifications,
    getUnreadNotifications,
    profileLoading,
    user,
    isAuthLoading,
    pathName,
    logError,
  ]);

  const isDev =
    process.env.NEXT_ENVIRONMENT === "local" ||
    process.env.NEXT_ENVIRONMENT === "development" ||
    process.env.NEXT_ENVIRONMENT === "stage";

  return (
    <Flex align={"center"} justify={"space-between"} w={"100%"} zIndex={1000}>
      <Flex
        align={pathName !== "/" ? "center" : "left"}
        gap={"12px"}
        mt={"4px"}
      >
        {pathName !== "/" ? (
          <Link href={"/"}>
            <Text
              {...h_700_16_100_32}
              color={"black"}
              borderBottom={"2px solid transparent"}
            >
              Home
            </Text>
          </Link>
        ) : (
          <></>
        )}
        <Dropdown
          textColor={"black"}
          titleButton={"Research"}
          menuList={menuListResearch({ isDev })}
          isSubMenu={true}
          customStyles={{}}
          minW={""}
          isProfile={false}
        />
      </Flex>
      {isList && !isCards ? <SearchCompanies /> : null}
      <Flex align={"center"} gap={"12px"}>
        {authStatus ? (
          <Flex align={"center"} gap={"12px"}>
            {pathName !== "/" &&
              !pathName?.includes("/search") &&
              !isMobile &&
              !isList && (
                <AuthInput
                  {...{
                    icon: <SearchSvg />,
                    iconLeftStyle: { left: "6px" },
                    input: {
                      h: "34px",
                      ps: "28px",
                      fontSize: "14px",
                      borderColor: "borderColor",
                      placeholder: "Search...",
                      _placeholder: { color: "gray" },
                      _focus: {
                        boxShadow: "none",
                      },
                      value: searchData || "",
                      onChange: (e) => setSearchData(e.target.value),
                      onKeyDown: (e) => handlerKeyDown(e),
                    },
                    iconHandler: onClickIconLeft,
                    hasError: false,
                    label: "",
                    labelBg: "",
                    styles: {},
                  }}
                />
              )}
            <NotificationMenu />
            <Dropdown
              isProfile={true}
              src={profile?.user_photo_upload as any}
              textColor={"black"}
              userName={
                profile?.first_name
                  ? ((
                      profile.first_name +
                      " " +
                      (profile.last_name || "")
                    ).trim() as any)
                  : false
              }
              menuList={menuListProfile}
              left={"-125px"}
              minW={"180px"}
              customStyles={{}}
              titleButton={""}
            />
          </Flex>
        ) : (
          <Flex align={"center"} gap={"8px"}>
            {pathName !== "/" &&
              !pathName?.includes("/search") &&
              !isMobile &&
              !isList && (
                <AuthInput
                  {...{
                    icon: <SearchSvg />,
                    iconLeftStyle: { left: "6px" },
                    input: {
                      h: "26px",
                      ps: "24px",
                      fontSize: "14px",
                      borderColor: "borderColor",
                      placeholder: "Search...",
                      _placeholder: { color: "gray", top: "4px" },
                      _focus: {
                        boxShadow: "none",
                      },
                      value: searchData || "",
                      onChange: (e) => setSearchData(e.target.value),
                      onKeyDown: (e) => handlerKeyDown(e),
                    },
                    iconHandler: onClickIconLeft,
                    hasError: false,
                    label: "",
                    labelBg: "",
                    styles: {},
                  }}
                />
              )}

            {/* 
              TODO: Temporary replacement of login/signup buttons with waitlist CTA 
              Restore original buttons when product launches in May 2025
              Original code:
              <Link href={"/sign-in"}>
                <ButtonDefault
                  title={"Login"}
                  rightIcon={<ArrowRightIcon />}
                  color={"mainBlue"}
                  hoverColor={"blue"}
                  hoverBorderColor={"blue"}
                  customStyles={{ borderRadius: "4px", p: "8px 13px 8px 18px" }}
                />
              </Link>
              <Link href={"/sign-up"} onClick={clearUserInfoHandler}>
                <ButtonDefault
                  backgroundColor={"mainBlue"}
                  title={"Create Free Account"}
                  hoverBgColor={"blue"}
                  hoverColor={"white"}
                  hoverBorderColor={"mainBlue"}
                  customStyles={{ borderRadius: "4px", p: "8px 18px" }}
                />
              </Link>
            */}
            <Flex align="center" gap={2}>
              <TestSupabaseAuthButton />
              <WaitlistCTANavButton />
            </Flex>
          </Flex>
        )}
      </Flex>
    </Flex>
  );
};
